import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || "http://localhost:8080";
const MCP_HEADER = { "X-MCP-Protocol": "1.0" };

export default function PropertyShares({ keycloak }) {
  const [shares, setShares] = useState([]);
  const [selectedShare, setSelectedShare] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [payments, setPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    tenantId: "",
    cardNumber: "",
    cardHolder: "",
    cardBrand: "VISA",
    amount: 0,
    installments: 1,
    paymentDate: new Date().toISOString().split("T")[0],
    notes: "",
  });

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${keycloak.token}`,
    ...MCP_HEADER,
  };

  const loadShares = async () => {
    const response = await fetch(`${API_URL}/api/shares`, { headers });
    const data = await response.json();
    setShares(data || []);
  };

  useEffect(() => {
    loadShares().catch((error) => console.error("Erro ao carregar cotas", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createShare = async () => {
    const propertyId = prompt("ID do Imóvel:");
    const shareType = prompt("Tipo (COTA, FRACAO, TIMESHARE):");
    const totalShares = prompt("Total de cotas:");
    const sharePrice = prompt("Preço por cota:");
    const monthlyFee = prompt("Taxa mensal:");
    if (propertyId && shareType) {
      await fetch(`${API_URL}/api/shares`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          propertyId,
          shareType,
          totalShares: Number(totalShares || 0),
          sharePrice: Number(sharePrice || 0),
          monthlyFee: Number(monthlyFee || 0),
        }),
      });
      loadShares();
    }
  };

  const loadPayments = async (shareId) => {
    const response = await fetch(`${API_URL}/api/payments/card/share/${shareId}`, { headers });
    const data = await response.json();
    setPayments(data || []);
  };

  const addShareholder = async (shareId) => {
    const name = prompt("Nome do cotista:");
    const document = prompt("CPF/CNPJ:");
    const email = prompt("E-mail:");
    const phone = prompt("Telefone:");
    const sharesOwned = prompt("Quantidade de cotas:");
    if (name && sharesOwned) {
      await fetch(`${API_URL}/api/shares/${shareId}/shareholders`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name,
          document,
          email,
          phone,
          sharesOwned: Number(sharesOwned),
        }),
      });
      loadShares();
      if (selectedShare === shareId) {
        const updated = shares.find(s => s.id === shareId);
        if (updated) setSelectedShare(updated);
      }
    }
  };

  const processPayment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/card`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...paymentForm,
          shareId: selectedShare.id,
        }),
      });
      const payment = await response.json();
      setPayments((prev) => [payment, ...prev]);
      setPaymentForm({
        tenantId: "",
        cardNumber: "",
        cardHolder: "",
        cardBrand: "VISA",
        amount: 0,
        installments: 1,
        paymentDate: new Date().toISOString().split("T")[0],
        notes: "",
      });
      setShowPaymentForm(false);
      alert(`Pagamento ${payment.status === "APROVADO" ? "aprovado" : "recusado"}! ${payment.authorizationCode ? `Código: ${payment.authorizationCode}` : ""}`);
    } catch (error) {
      console.error("Erro ao processar pagamento", error);
      alert("Erro ao processar pagamento");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <section className="hero">
        <h1>Gerenciamento de Cotas Imobiliárias</h1>
        <p>
          Gerencie cotas, frações e timeshares de imóveis com controle de cotistas e investimentos.
        </p>
      </section>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "2rem", flexWrap: "wrap" }}>
        <button
          className={activeTab === "list" ? "btn-primary" : "btn-secondary"}
          onClick={() => setActiveTab("list")}
        >
          Cotas Disponíveis
        </button>
        {selectedShare && (
          <button
            className={activeTab === "details" ? "btn-primary" : "btn-secondary"}
            onClick={() => setActiveTab("details")}
          >
            Detalhes
          </button>
        )}
      </div>

      {activeTab === "list" && (
        <section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
            <h2 className="section-title" style={{ margin: 0 }}>Cotas Cadastradas</h2>
            <button className="btn-primary" onClick={createShare}>
              + Nova Cota
            </button>
          </div>
          <div className="grid grid-3">
            {shares.map((share) => (
              <div key={share.id} className="listing-card">
                <div className="tag">{share.shareType}</div>
                <h4>Imóvel: {share.propertyId}</h4>
                <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--brand-blue)", margin: "0.75rem 0" }}>
                  R$ {share.sharePrice?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}/cota
                </div>
                <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                  <strong>Total:</strong> {share.totalShares} cotas
                </div>
                <div style={{ color: "var(--text-secondary)" }}>
                  <strong>Disponíveis:</strong> {share.availableShares} cotas
                </div>
                {share.monthlyFee > 0 && (
                  <div style={{ color: "var(--text-secondary)" }}>
                    <strong>Taxa mensal:</strong> R$ {share.monthlyFee?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </div>
                )}
                <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                  <strong>Cotistas:</strong> {share.shareholders?.length || 0}
                </div>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setSelectedShare(share);
                    setActiveTab("details");
                    loadPayments(share.id);
                  }}
                  style={{ marginTop: "1rem", width: "100%" }}
                >
                  Ver Detalhes
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {activeTab === "details" && selectedShare && (
        <section>
          <h2 className="section-title">Detalhes da Cota - {selectedShare.shareType}</h2>
          <div className="panel">
            <div style={{ marginBottom: "1.5rem" }}>
              <h3>Informações Gerais</h3>
              <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                <strong>Imóvel:</strong> {selectedShare.propertyId}
              </div>
              <div style={{ color: "var(--text-secondary)" }}>
                <strong>Preço por cota:</strong> R$ {selectedShare.sharePrice?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </div>
              <div style={{ color: "var(--text-secondary)" }}>
                <strong>Total de cotas:</strong> {selectedShare.totalShares}
              </div>
              <div style={{ color: "var(--text-secondary)" }}>
                <strong>Cotas disponíveis:</strong> {selectedShare.availableShares}
              </div>
              {selectedShare.monthlyFee > 0 && (
                <div style={{ color: "var(--text-secondary)" }}>
                  <strong>Taxa mensal:</strong> R$ {selectedShare.monthlyFee?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </div>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3>Cotistas</h3>
                <button className="btn-primary" onClick={() => addShareholder(selectedShare.id)}>
                  + Adicionar Cotista
                </button>
              </div>
              {selectedShare.shareholders && selectedShare.shareholders.length > 0 ? (
                <div className="grid grid-3">
                  {selectedShare.shareholders.map((shareholder) => (
                    <div key={shareholder.id} className="listing-card">
                      <h4>{shareholder.name}</h4>
                      <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                        <strong>Cotas:</strong> {shareholder.sharesOwned}
                      </div>
                      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "var(--brand-green)", margin: "0.75rem 0" }}>
                        R$ {shareholder.investmentAmount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>
                      {shareholder.document && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                          CPF/CNPJ: {shareholder.document}
                        </div>
                      )}
                      {shareholder.email && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                          📧 {shareholder.email}
                        </div>
                      )}
                      {shareholder.phone && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                          📞 {shareholder.phone}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="panel" style={{ textAlign: "center", padding: "2rem" }}>
                  <p style={{ color: "var(--text-secondary)" }}>Nenhum cotista cadastrado ainda.</p>
                </div>
              )}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3>Pagamentos via Cartão</h3>
                <button className="btn-primary" onClick={() => setShowPaymentForm(true)}>
                  💳 Novo Pagamento
                </button>
              </div>
              {showPaymentForm && (
                <div className="panel" style={{ marginBottom: "1.5rem", background: "#f8fafc" }}>
                  <h4 style={{ marginBottom: "1rem" }}>Processar Pagamento</h4>
                  <div className="grid grid-3">
                    <input
                      placeholder="ID do Locatário"
                      value={paymentForm.tenantId}
                      onChange={(e) => setPaymentForm({ ...paymentForm, tenantId: e.target.value })}
                    />
                    <input
                      placeholder="Número do cartão"
                      value={paymentForm.cardNumber}
                      onChange={(e) => setPaymentForm({ ...paymentForm, cardNumber: e.target.value })}
                      maxLength={19}
                    />
                    <input
                      placeholder="Nome no cartão"
                      value={paymentForm.cardHolder}
                      onChange={(e) => setPaymentForm({ ...paymentForm, cardHolder: e.target.value })}
                    />
                    <select
                      value={paymentForm.cardBrand}
                      onChange={(e) => setPaymentForm({ ...paymentForm, cardBrand: e.target.value })}
                    >
                      <option value="VISA">Visa</option>
                      <option value="MASTERCARD">Mastercard</option>
                      <option value="ELO">Elo</option>
                      <option value="AMEX">American Express</option>
                    </select>
                    <input
                      type="number"
                      placeholder="Valor"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })}
                    />
                    <input
                      type="number"
                      placeholder="Parcelas"
                      value={paymentForm.installments}
                      onChange={(e) => setPaymentForm({ ...paymentForm, installments: Number(e.target.value) || 1 })}
                      min="1"
                      max="12"
                    />
                    <input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                    />
                    <textarea
                      placeholder="Observações"
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                      style={{ gridColumn: "1 / -1" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                    <button className="btn-primary" onClick={processPayment} disabled={loading}>
                      {loading ? "Processando..." : "Processar Pagamento"}
                    </button>
                    <button className="btn-secondary" onClick={() => setShowPaymentForm(false)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
              {payments.length > 0 ? (
                <div className="grid grid-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="listing-card">
                      <div
                        className="tag"
                        style={{
                          background:
                            payment.status === "APROVADO"
                              ? "var(--brand-green)20"
                              : payment.status === "RECUSADO"
                              ? "#dc262620"
                              : "#f59e0b20",
                          color:
                            payment.status === "APROVADO"
                              ? "var(--brand-green)"
                              : payment.status === "RECUSADO"
                              ? "#dc2626"
                              : "#f59e0b",
                        }}
                      >
                        {payment.status}
                      </div>
                      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--brand-blue)", margin: "0.75rem 0" }}>
                        R$ {payment.amount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                      </div>
                      {payment.installments > 1 && (
                        <div style={{ color: "var(--text-secondary)" }}>
                          {payment.installments}x de R$ {payment.installmentAmount?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </div>
                      )}
                      <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                        <strong>Cartão:</strong> {payment.cardBrand} •••• {payment.cardNumber}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>
                        <strong>Titular:</strong> {payment.cardHolder}
                      </div>
                      {payment.authorizationCode && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                          <strong>Autorização:</strong> {payment.authorizationCode}
                        </div>
                      )}
                      {payment.transactionId && (
                        <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                          <strong>Transação:</strong> {payment.transactionId}
                        </div>
                      )}
                      <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem", marginTop: "0.5rem" }}>
                        Data: {payment.paymentDate}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="panel" style={{ textAlign: "center", padding: "2rem" }}>
                  <p style={{ color: "var(--text-secondary)" }}>Nenhum pagamento registrado ainda.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
